// utils/common/deviceHelpers.ts

// Policy: device_id is the fingerprint of the public key (SHA-256 of SPKI).
// We derive it from IndexedDB (public key PEM), then mirror to LS & Cookie for fast sync reads.

import Cookies from "js-cookie";
import { secureWipeAllKeys, getPublicKeyPem } from "../crypto/keyStorageSecure";
import { computeKeyIdFromPublicPem } from "../crypto/keyUtils";

const DEVICE_ID_KEY = "device_id";

const isBrowser = () => typeof window !== "undefined";
// NOTE: We normalize to lowercase for consistency across client/server (we treat the id as opaque).
const normalizeId = (id: string) => (id || "").trim().toLowerCase();

const setDeviceIdCookie = (deviceId: string) => {
  if (!isBrowser()) return;
  const secure = window.location.protocol === "https:";
  Cookies.set(DEVICE_ID_KEY, deviceId, {
    expires: 365,
    sameSite: "Strict",
    secure,
    path: "/",
  });
};

/**
 * Build canonical device_id from the local public key (IndexedDB),
 * then mirror it to LS & Cookie for compatibility with sync code paths.
 */
export const computeCanonicalDeviceId = async (): Promise<string> => {
  if (!isBrowser()) return "server";
  const pub = await getPublicKeyPem();
  if (!pub) {
    // Caller must ensure keys exist (generate or restore) before computing device_id.
    throw new Error("Public key not found. Ensure keys exist before computing device_id.");
  }
  const fidRaw = await computeKeyIdFromPublicPem(pub); // SHA-256(SPKI) → base64url
  const fid = normalizeId(fidRaw);
  localStorage.setItem(DEVICE_ID_KEY, fid);
  setDeviceIdCookie(fid);
  return fid;
};

/**
 * Ensure LS/Cookie contain the *canonical* device_id.
 * - If cached exists but differs from current key fingerprint (e.g., after rotation/restore), refresh it.
 * - If keys are missing, fall back to cached (if any) or "unknown".
 */
export const ensureDeviceIdReady = async (): Promise<string> => {
  if (!isBrowser()) return "server";

  const cachedRaw = (localStorage.getItem(DEVICE_ID_KEY) || Cookies.get(DEVICE_ID_KEY) || "").trim();
  let cached = cachedRaw ? normalizeId(cachedRaw) : "";

  try {
    const pub = await getPublicKeyPem();
    if (pub) {
      const fid = normalizeId(await computeKeyIdFromPublicPem(pub));
      if (fid !== cached) {
        localStorage.setItem(DEVICE_ID_KEY, fid);
        setDeviceIdCookie(fid);
      }
      return fid;
    }
  } catch {
    // ignore; fall back to cached
  }

  // No public key available → return cached if present, otherwise "unknown"
  return cached || "unknown";
};

/**
 * Synchronous accessor used by existing code.
 * IMPORTANT: Prefer calling an async accessor (e.g., getDeviceIdAsync from keyManager) once per session,
 *            which internally calls ensureDeviceIdReady() to keep this value canonical.
 */
export const getDeviceId = (): string => {
  if (!isBrowser()) return "server";
  const existing = (localStorage.getItem(DEVICE_ID_KEY) || Cookies.get(DEVICE_ID_KEY) || "").trim();
  return existing ? normalizeId(existing) : "unknown";
};

/**
 * Manual setter (discouraged). Prefer deriving from keys via `ensureDeviceIdReady`.
 * Kept only for backward compatibility in rare cases.
 */
export const setDeviceId = (deviceId: string) => {
  if (!isBrowser() || !deviceId) return;
  const normalized = normalizeId(deviceId);
  localStorage.setItem(DEVICE_ID_KEY, normalized);
  setDeviceIdCookie(normalized);
};

/**
 * Clear mirrored device_id. If `hardReset` is true, wipe all local key material, too.
 * Note: wiping keys will force a new device_id until the user restores from backup.
 */
export const clearDeviceId = async (hardReset = false): Promise<void> => {
  if (!isBrowser()) return;
  localStorage.removeItem(DEVICE_ID_KEY);
  Cookies.remove(DEVICE_ID_KEY, { path: "/" });
  if (hardReset) {
    await secureWipeAllKeys();
  }
};

/** Compare given device_id with the current mirrored value. */
export const isCurrentDevice = (deviceId: string): boolean => {
  if (!isBrowser()) return false;
  const current = localStorage.getItem(DEVICE_ID_KEY) || "";
  return normalizeId(current) === normalizeId(deviceId);
};
